import type { TasksKanbanTask } from '@entities/task'
import type { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import dayjs from 'dayjs'

import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute, TasksKanbanRelatedToType } from '@shared/config'
import { useCanonicalRoute } from '@shared/lib/routes'

interface TasksKanbanStageReturn {
  isExpired: (deadline: string | null) => boolean
  isUpcoming: (deadline: string | null) => boolean
  getAgentName: (task: TasksKanbanTask) => string | null
  getDisciplineName: (task: TasksKanbanTask) => Discipline | null
  getDisciplineUrl: (task: TasksKanbanTask) => string | null
  getKidName: (task: TasksKanbanTask) => string | null
  getRelatedToType: (task: TasksKanbanTask) => TasksKanbanRelatedToType | null
  getSubscriptionId: (task: TasksKanbanTask) => number | null
  getSubscriptionUrl: (task: TasksKanbanTask) => string | null
  getTaskName: (task: TasksKanbanTask) => string
  getTaskUrl: (task: TasksKanbanTask) => string
  getPriorityLabel: (task: TasksKanbanTask) => string
  handleCardClick: () => void
}

export const useTasksKanbanStage = (): TasksKanbanStageReturn => {
  const listsStore = useListsStore()
  const { saveCanonicalUrl } = useCanonicalRoute({ key: 'tasks-kanban-referrer' })

  const isExpired = (deadline: string | null): boolean => {
    if (!deadline) return false
    return dayjs(deadline).isBefore(dayjs())
  }

  const isUpcoming = (deadline: string | null): boolean => {
    if (!deadline || isExpired(deadline)) return false
    return dayjs(deadline).diff(dayjs(), 'hour') <= 24
  }

  const getAgentName = (task: TasksKanbanTask): string | null => {
    return task.assignee?.name ?? null
  }

  const getTaskName = (task: TasksKanbanTask): string => {
    return listsStore.getLists?.tasks_kanban.lists.task_type[task.task_type] || task.task_type
  }

  const getDisciplineName = (task: TasksKanbanTask): Discipline | null => {
    return task.related_to?.code as Discipline || null
  }

  const getDisciplineUrl = (task: TasksKanbanTask): string | null => {
    if (task.related_to?.id && task.related_to?.code) {
      return resolveUrl(AppRoute.DisciplinesGeneralInfo, { id: task.related_to.id })
    }
    return null
  }

  const getKidName = (task: TasksKanbanTask): string | null => {
    return task.related_to?.kid?.name || null
  }

  const getRelatedToType = (task: TasksKanbanTask): TasksKanbanRelatedToType | null => {
    const type = task.related_to_type as TasksKanbanRelatedToType
    return Object.values(TasksKanbanRelatedToType).includes(type) ? type : null
  }

  const getSubscriptionId = (task: TasksKanbanTask): number | null => {
    if (getRelatedToType(task) !== TasksKanbanRelatedToType.Subscription) return null
    return task.related_to?.id ?? null
  }

  const getSubscriptionUrl = (task: TasksKanbanTask): string | null => {
    if (getRelatedToType(task) !== TasksKanbanRelatedToType.Subscription) return null
    const customerId = task.customer?.id
    if (!customerId) return null
    return resolveUrl(AppRoute.CustomersSubscriptions, { id: customerId })
  }

  const getPriorityLabel = (task: TasksKanbanTask): string => {
    if (!task.priority) return ''
    return listsStore.getTasksKanbanList?.priority?.[task.priority] || task.priority
  }

  const getTaskUrl = (task: TasksKanbanTask): string => {
    return resolveUrl(AppRoute.TasksKanbanInfo, { id: task.id }) ?? ''
  }

  const handleCardClick = (): void => {
    saveCanonicalUrl()
  }

  return {
    isExpired,
    isUpcoming,
    getAgentName,
    getDisciplineName,
    getDisciplineUrl,
    getTaskName,
    getTaskUrl,
    getKidName,
    getRelatedToType,
    getSubscriptionId,
    getSubscriptionUrl,
    getPriorityLabel,
    handleCardClick,
  }
}
